import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Globe, Mail, Phone, Clock, BookOpen, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import HubSpotForm from "./HubSpotForm";
import { useEffect } from "react";

const Contact = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const contactItems = [
        {
            icon: Globe,
            label: "Website",
            value: "www.expressanalytics.com",
            href: "http://www.expressanalytics.com",
            color: "from-blue-400 to-cyan-500"
        },
        {
            icon: Mail,
            label: "Email",
            value: "info@expressanalytics.com",
            href: "mailto:info@expressanalytics.com",
            color: "from-green-400 to-emerald-500"
        },
        {
            icon: Phone,
            label: "Phone",
            value: "+1 (618) 224 3573",
            href: "tel:+16182243573",
            color: "from-purple-400 to-violet-500"
        },
        {
            icon: Clock,
            label: "Support Hours",
            value: "Mon–Fri: 9am–6pm EST",
            color: "from-orange-400 to-red-500"
        },
        {
            icon: BookOpen,
            label: "Resources",
            value: "Read our blog",
            href: "https://www.expressanalytics.com/resources/blogs",
            color: "from-pink-400 to-rose-500"
        }
    ];

    return (
        <div className="min-h-screen bg-background dark:bg-black dark:text-white relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-chart-1/20 to-chart-2/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
            </div>

            {/* Hero Section */}
            <motion.section
                className="relative overflow-hidden py-24 px-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="relative max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <motion.div
                            className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent/10 to-primary/10 backdrop-blur-sm border border-accent/20 rounded-full px-6 py-3 mb-8"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        >
                            <Sparkles className="w-5 h-5 text-accent" />
                            <span className="text-sm font-medium text-accent">Let's Connect</span>
                        </motion.div>

                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
                            Get in
                            <span className="block   bg-gradient-to-r from-accent via-primary to-chart-1 bg-clip-text">
                                Touch
                            </span>
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Ready to unlock the power of your data? Let's discuss how our analytics solutions can transform your business with cutting-edge insights.
                        </p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Contact Form Section */}
            <motion.section
                className="px-6 pb-24"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <motion.div
                            whileHover={{ y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        >
                            <Card className="border-0 bg-card/60 backdrop-blur-xl shadow-2xl relative overflow-hidden group dark:bg-[#ea3036]">
                                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <CardHeader className="relative z-10">
                                    <CardTitle className="text-2xl flex items-center space-x-3">
                                        <div className="p-2 bg-gradient-to-r from-accent to-primary rounded-lg">
                                            <Mail className="w-6 h-6 text-white" />
                                        </div>
                                        <span>Send us a message</span>
                                    </CardTitle>
                                    <p className="text-muted-foreground">
                                        Fill out the form below and we'll get back to you as soon as possible.
                                    </p>
                                </CardHeader>
                                <CardContent className="relative z-10 dark:bg-gray-100 pt-4">
                                    <HubSpotForm />
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Contact Information */}
                        <div className="space-y-6">
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                            >
                                <Card className="border-0 bg-card/60 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-gradient-to-br from-chart-1/5 to-chart-2/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <CardHeader className="relative z-10">
                                        <CardTitle className="text-2xl flex items-center space-x-3">
                                            <div className="p-2 bg-gradient-to-r from-chart-1 to-chart-2 rounded-lg">
                                                <Phone className="w-6 h-6 text-white" />
                                            </div>
                                            <span>Contact Information</span>
                                        </CardTitle>
                                        <p className="text-muted-foreground">
                                            Multiple ways to reach our team of experts.
                                        </p>
                                    </CardHeader>
                                    <CardContent className="space-y-6 relative z-10">
                                        {contactItems.map((item, index) => {
                                            const IconComponent = item.icon;
                                            return (
                                                <motion.div
                                                    key={index}
                                                    className="group/item"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                                                    whileHover={{ x: 8 }}
                                                >
                                                    <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-muted/50 transition-all duration-300">
                                                        <motion.div
                                                            className={`p-3 rounded-xl bg-gradient-to-r ${item.color} shadow-lg`}
                                                            whileHover={{ scale: 1.1, rotate: 5 }}
                                                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                                        >
                                                            <IconComponent className="w-5 h-5 text-white" />
                                                        </motion.div>
                                                        <div className="flex-1">
                                                            <p className="text-sm text-muted-foreground">{item.label}</p>
                                                            {item.href ? (
                                                                <a
                                                                    href={item.href}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="font-medium text-accent hover:text-accent/80 transition-colors flex items-center space-x-2 group/link"
                                                                >
                                                                    <span>{item.value}</span>
                                                                    <ArrowRight className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transform translate-x-0 group-hover/link:translate-x-1 transition-all duration-200" />
                                                                </a>
                                                            ) : (
                                                                <p className="font-medium">{item.value}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {index < contactItems.length - 1 && (
                                                        <Separator className="my-2 opacity-50" />
                                                    )}
                                                </motion.div>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            </motion.div>

                            {/* Call to Action */}
                            {/* <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <Card className="border-0 bg-gradient-to-r from-accent/10 to-primary/10 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <CardContent className="p-8 text-center relative z-10">
                                        <motion.div
                                            animate={{
                                                rotate: [0, 10, -10, 0],
                                            }}
                                            transition={{
                                                duration: 4,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        >
                                            <Sparkles className="w-12 h-12 text-accent mx-auto mb-4" />
                                        </motion.div>
                                        <h3 className="text-xl font-bold mb-2 gradient-text bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                                            Ready to Get Started?
                                        </h3>
                                        <p className="text-muted-foreground mb-6">
                                            Join hundreds of companies already using our data science solutions.
                                        </p>
                                        <Button
                                            variant="gradient"
                                            size="lg"
                                            className="shadow-lg hover:shadow-xl"
                                        >
                                            Schedule a Demo
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div> */}
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default Contact;